<script setup lang="ts">
  import { IconAccount } from '@gat-ship/icons'
  import type { TenantUser } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    FormField,
    FormSubmit,
    InputGroupInput,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { useTenantUserDetailContext } from '../../composables/useTenantUserDetail'

  const isOpen = ref(false)
  const tenantUserProvider = useTenantUserDetailContext()!
  const description = ref('')

  const changeNameSchema = z.object({
    userId: z.string().min(1),
    fullName: z.string().min(1, 'Full Name is required'),
    initials: z
      .string()
      .min(1, 'Initials are required')
      .max(3, 'Max 3 characters')
  })
  type ChangeNameFormValues = z.infer<typeof changeNameSchema>

  const form = useForm({
    defaultValues: {
      userId: '',
      fullName: '',
      initials: ''
    } as ChangeNameFormValues,
    validators: {
      onChange: changeNameSchema
    },
    onSubmit: async ({ value }) => {
      await tenantUserProvider.commands.changeName({
        userId: value.userId,
        fullName: value.fullName,
        initials: value.initials
      })
      isOpen.value = false
    }
  })

  export interface ShowChangeNameModalParams {
    tenantUser: TenantUser
  }

  function showModal({ tenantUser }: ShowChangeNameModalParams) {
    description.value = tenantUser.fullName
    form.reset({
      userId: tenantUser.userId,
      fullName: tenantUser.fullName,
      initials: tenantUser.initials
    })
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconAccount class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        Change Name
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="description">
        {{ description }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField :form="form" name="fullName" label="Full Name" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                autofocus
                :data-testid="testIds.tenantUser.modal.changeName.fullNameInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField :form="form" name="initials" label="Initials" required>
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="testIds.tenantUser.modal.changeName.initialsInput"
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
